import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { ensureCsrfToken } from '../utils/csrf';
import { useNotification } from '../contexts/NotificationContext';
import SEOHead from '../components/SEOHead';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { GeoRegion, REGION_LABELS, COUNTRIES_BY_REGION } from '../data/countries';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface AdminUserRow {
  id: string;
  username: string;
  email: string;
  geo_region: string | null;
  country: string | null;
  country_code: string | null;
  is_premium: boolean;
  is_admin: boolean;
  is_banned: boolean;
  banned_at: string | null;
  is_bot: boolean;
  total_xp: number | null;
  current_level: number | null;
  failed_login_attempts: number | null;
  locked_until: string | null;
  last_login_at: string | null;
  created_at: string;
  casual_quizzes_count?: number;
  competitive_quizzes_count?: number;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface UsersListResponse {
  success: boolean;
  data: { users: AdminUserRow[]; pagination: Pagination };
  message?: string;
}

interface BadgeCatalogItem {
  id: string;
  name: string;
  description: string;
  category: string;
  rarity: string;
}

interface UserBadgeItem {
  id: string;
  userId: string;
  badgeId: string;
  earnedAt: string;
  isFeatured: boolean;
  currentLevel: string | null;
  badge?: any;
}

interface UserBadgesResponse {
  success: boolean;
  data: { catalog: BadgeCatalogItem[]; userBadges: UserBadgeItem[]; featured: UserBadgeItem[] };
  message?: string;
}

const formatDateTime = (raw: string | null | undefined): string => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString();
};

const isLocked = (u: AdminUserRow): boolean => {
  if (!u.locked_until) return false;
  const d = new Date(u.locked_until);
  return !Number.isNaN(d.getTime()) && d.getTime() > Date.now();
};

const AdminUsers: React.FC = () => {
  useDocumentTitle('Cognidaily | Admin Users');

  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [includeBots, setIncludeBots] = useState(false);
  const [adminOnly, setAdminOnly] = useState(false);
  const [banned, setBanned] = useState<'all' | 'true' | 'false'>('all');
  const [locked, setLocked] = useState<'all' | 'true' | 'false'>('all');
  const [premium, setPremium] = useState<'all' | 'true' | 'false'>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activeUser, setActiveUser] = useState<AdminUserRow | null>(null);
  const [drawerTab, setDrawerTab] = useState<'profile' | 'security' | 'badges' | 'danger'>(
    'profile'
  );
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const { showSuccess, showError } = useNotification();

  const [badgesCatalog, setBadgesCatalog] = useState<BadgeCatalogItem[]>([]);
  const [userBadges, setUserBadges] = useState<UserBadgeItem[]>([]);
  const [badgeGrantId, setBadgeGrantId] = useState<string>('');
  const [growthData, setGrowthData] = useState<Array<{ date: string; totalUsers: number; totalPremiumUsers: number }>>([]);
  const [growthDays, setGrowthDays] = useState<7 | 30 | 90 | 365>(30);
  const [growthLoading, setGrowthLoading] = useState(false);
  const [summaryMetrics, setSummaryMetrics] = useState<{
    totalUsers: number;
    activeLast30Days: number;
  } | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const params = useMemo(() => {
    const p: Record<string, any> = {
      page,
      limit: 50,
      includeBots
    };
    if (search.trim()) p.search = search.trim();
    if (adminOnly) p.adminOnly = 'true';
    if (banned !== 'all') p.banned = banned;
    if (locked !== 'all') p.locked = locked;
    if (premium !== 'all') p.premium = premium;
    return p;
  }, [page, search, includeBots, adminOnly, banned, locked, premium]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get<UsersListResponse>('/api/admin/users', { params });
      if (!res.data.success) throw new Error(res.data.message || 'Failed to load users');
      setUsers(res.data.data.users || []);
      setPagination(res.data.data.pagination);
    } catch (err: any) {
      console.error('[AdminUsers] Failed to load users', err);
      setError(err?.response?.data?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const openUser = async (u: AdminUserRow) => {
    setActiveUser(u);
    setDrawerTab('profile');
    setBadgesCatalog([]);
    setUserBadges([]);
    setBadgeGrantId('');
  };

  const refreshActiveUser = async () => {
    if (!activeUser) return;
    try {
      const res = await axios.get<{ success: boolean; data: { user: AdminUserRow } }>(
        `/api/admin/users/${activeUser.id}`
      );
      if (res.data.success && res.data.data?.user) {
        setActiveUser(res.data.data.user);
      }
    } catch {
      // ignore
    }
  };

  const updateUser = async (updates: Partial<AdminUserRow>) => {
    if (!activeUser) return;
    try {
      setActionBusy('update');
      const res = await axios.put<{ success: boolean; data?: { user: AdminUserRow }; message?: string }>(
        `/api/admin/users/${activeUser.id}`,
        updates
      );
      if (!res.data.success) throw new Error(res.data.message || 'Update failed');
      if (res.data.data?.user) setActiveUser(res.data.data.user);
      showSuccess(res.data.message || 'User updated.');
      await loadUsers();
    } catch (err: any) {
      showError(err?.response?.data?.message || err.message || 'Update failed.');
    } finally {
      setActionBusy(null);
    }
  };

  const runAction = async (key: string, fn: () => Promise<void>) => {
    try {
      setActionBusy(key);
      await fn();
      await refreshActiveUser();
      await loadUsers();
    } catch (err: any) {
      console.error('[AdminUsers] Action failed', err);
      showError(err?.response?.data?.message || err.message || 'Action failed.');
    } finally {
      setActionBusy(null);
    }
  };

  const loadBadges = useCallback(async () => {
    if (!activeUser) return;
    try {
      setActionBusy('badges_load');
      const res = await axios.get<UserBadgesResponse>(`/api/admin/users/${activeUser.id}/badges`);
      if (!res.data.success) throw new Error(res.data.message || 'Failed to load badges');
      setBadgesCatalog(res.data.data.catalog || []);
      setUserBadges(res.data.data.userBadges || []);
    } catch (err: any) {
      showError(err?.response?.data?.message || 'Failed to load badges.');
    } finally {
      setActionBusy(null);
    }
  }, [activeUser]);

  useEffect(() => {
    if (drawerTab === 'badges' && activeUser) {
      loadBadges();
    }
  }, [drawerTab, activeUser, loadBadges]);

  const downloadExport = async () => {
    if (!activeUser) return;
    const res = await axios.get<{ success: boolean; data: any }>(`/api/admin/users/${activeUser.id}/export`);
    if (!res.data.success) throw new Error('Export failed');
    const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user-export-${activeUser.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && activeUser) {
        setActiveUser(null);
      }
    };
    if (activeUser) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [activeUser]);

  // Get available countries for selected region
  const availableCountries = useMemo(() => {
    if (!activeUser?.geo_region) return [];
    const region = activeUser.geo_region as GeoRegion;
    return COUNTRIES_BY_REGION[region] || [];
  }, [activeUser?.geo_region]);

  // Load user growth data
  const loadGrowthData = async () => {
    try {
      setGrowthLoading(true);
      const res = await axios.get<{
        success: boolean;
        data: Array<{ date: string; totalUsers: number; totalPremiumUsers: number }>;
      }>(`/api/admin/metrics/users/growth`, {
        params: { days: growthDays, includeBots: includeBots }
      });
      if (res.data.success) {
        // Format dates for display and ensure values are numbers
        const formatted = res.data.data.map((point) => ({
          date: new Date(point.date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          }),
          totalUsers: typeof point.totalUsers === 'number' ? point.totalUsers : parseInt(String(point.totalUsers), 10) || 0,
          totalPremiumUsers: typeof point.totalPremiumUsers === 'number' ? point.totalPremiumUsers : parseInt(String(point.totalPremiumUsers), 10) || 0
        }));
        setGrowthData(formatted);
      }
    } catch (err) {
      console.error('[AdminUsers] Failed to load growth data', err);
    } finally {
      setGrowthLoading(false);
    }
  };

  useEffect(() => {
    loadGrowthData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [growthDays, includeBots]);

  useEffect(() => {
    const loadSummaryMetrics = async () => {
      try {
        setSummaryLoading(true);
        const res = await axios.get<{
          success: boolean;
          data?: {
            totalUsers: number;
            activeLast30Days: number;
            activeLast7Days: number;
            activeToday: number;
            totalPremiumUsers: number;
            totalAdminUsers: number;
          };
        }>('/api/admin/metrics/users/summary', {
          params: { includeBots }
        });
        if (res.data.success && res.data.data) {
          setSummaryMetrics({
            totalUsers: res.data.data.totalUsers || 0,
            activeLast30Days: res.data.data.activeLast30Days || 0
          });
        }
      } catch (err) {
        console.error('[AdminUsers] Failed to load summary metrics', err);
        setSummaryMetrics({ totalUsers: 0, activeLast30Days: 0 });
      } finally {
        setSummaryLoading(false);
      }
    };
    loadSummaryMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeBots]);

  return (
    <div className="admin-page">
      <SEOHead title="Admin | Users" description="Admin tools for viewing and managing users." />
      <div>
        <section>
          <header className="admin-page__header">
            <h1 className="admin-page__title">Users</h1>
            <p className="admin-page__subtitle">
              Search, filter, and manage user accounts (roles, bans, locks, passwords, badges).
            </p>
          </header>

          {/* Summary Cards */}
          <div className="admin-summary-cards">
            <div className="admin-summary-card">
              <div className="admin-summary-card__label">Total Users</div>
              <div className="admin-summary-card__value">
                {summaryLoading ? '...' : summaryMetrics?.totalUsers.toLocaleString('en-US') || '0'}
              </div>
            </div>
            <div className="admin-summary-card">
              <div className="admin-summary-card__label">Active (30d)</div>
              <div className="admin-summary-card__value">
                {summaryLoading ? '...' : summaryMetrics?.activeLast30Days.toLocaleString('en-US') || '0'}
              </div>
            </div>
          </div>

          {/* User Growth Graph */}
          <div className="admin-usage-card">
            <div className="admin-section-card__header">
              <h2 className="admin-section__title">User Growth</h2>
              <div className="admin-page__presets">
                {([7, 30, 90, 365] as const).map((days) => (
                  <button
                    key={days}
                    type="button"
                    className={`btn btn-ghost btn-xs ${growthDays === days ? 'btn-ghost--active' : ''}`}
                    onClick={() => setGrowthDays(days)}
                  >
                    {days}d
                  </button>
                ))}
              </div>
            </div>
            <div style={{ minHeight: 200 }}>
              {growthLoading ? (
                <div className="admin-loading">Loading…</div>
              ) : growthData.length === 0 ? (
                <p className="admin-loading">No data</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={growthData} margin={{ top: 10, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-subtle, #e5e7eb)" />
                    <XAxis
                      dataKey="date"
                      stroke="var(--text-muted, #6b7280)"
                      tick={{ fill: 'var(--text-muted, #6b7280)', fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      stroke="var(--text-muted, #6b7280)"
                      tick={{ fill: 'var(--text-muted, #6b7280)', fontSize: 12 }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--surface-elevated, #ffffff)',
                        border: '1px solid var(--surface-subtle, #e5e7eb)',
                        borderRadius: '0.5rem',
                        color: 'var(--color-text, #0f172a)'
                      }}
                      formatter={(value: any, name: string) => {
                        // Ensure we parse as integer base 10
                        const numValue = typeof value === 'number' ? Math.floor(value) : parseInt(String(value).replace(/[^0-9-]/g, ''), 10);
                        if (isNaN(numValue) || numValue < 0) return [value, name === 'totalUsers' ? 'Total Users' : 'Premium Users'];
                        const label = name === 'totalUsers' ? 'Total Users' : 'Premium Users';
                        return [numValue.toLocaleString('en-US'), label];
                      }}
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: '10px' }}
                      formatter={(value) => value === 'totalUsers' ? 'Total Users' : 'Premium Users'}
                    />
                    <Line
                      type="monotone"
                      dataKey="totalUsers"
                      stroke="#6366f1"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                      name="totalUsers"
                    />
                    <Line
                      type="monotone"
                      dataKey="totalPremiumUsers"
                      stroke="#d4af37"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                      name="totalPremiumUsers"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="admin-filters">
            <input
              className="admin-page__search"
              placeholder="Search username or email…"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
            />
            <label className="admin-checkbox admin-users-filter">
              <input
                type="checkbox"
                checked={includeBots}
                onChange={(e) => {
                  setPage(1);
                  setIncludeBots(e.target.checked);
                }}
              />
              Include bots
            </label>
            <label className="admin-checkbox admin-users-filter">
              <input
                type="checkbox"
                checked={adminOnly}
                onChange={(e) => {
                  setPage(1);
                  setAdminOnly(e.target.checked);
                }}
              />
              Admins only
            </label>
            <div className="admin-users-filter-group">
              <label className="admin-users-filter-label">Premium:</label>
              <div className="admin-users-toggle" role="group" aria-label="Premium filter">
                <button
                  type="button"
                  className={`admin-users-toggle-btn ${premium === 'all' ? 'admin-users-toggle-btn--active' : ''}`}
                  onClick={() => {
                    setPage(1);
                    setPremium('all');
                  }}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`admin-users-toggle-btn ${premium === 'true' ? 'admin-users-toggle-btn--active' : ''}`}
                  onClick={() => {
                    setPage(1);
                    setPremium('true');
                  }}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={`admin-users-toggle-btn ${premium === 'false' ? 'admin-users-toggle-btn--active' : ''}`}
                  onClick={() => {
                    setPage(1);
                    setPremium('false');
                  }}
                >
                  No
                </button>
              </div>
            </div>
            <div className="admin-users-filter-group">
              <label className="admin-users-filter-label">Banned:</label>
              <div className="admin-users-toggle" role="group" aria-label="Banned filter">
                <button
                  type="button"
                  className={`admin-users-toggle-btn ${banned === 'all' ? 'admin-users-toggle-btn--active' : ''}`}
                  onClick={() => {
                    setPage(1);
                    setBanned('all');
                  }}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`admin-users-toggle-btn ${banned === 'true' ? 'admin-users-toggle-btn--active' : ''}`}
                  onClick={() => {
                    setPage(1);
                    setBanned('true');
                  }}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={`admin-users-toggle-btn ${banned === 'false' ? 'admin-users-toggle-btn--active' : ''}`}
                  onClick={() => {
                    setPage(1);
                    setBanned('false');
                  }}
                >
                  No
                </button>
              </div>
            </div>
            <div className="admin-users-filter-group">
              <label className="admin-users-filter-label">Locked:</label>
              <div className="admin-users-toggle" role="group" aria-label="Locked filter">
                <button
                  type="button"
                  className={`admin-users-toggle-btn ${locked === 'all' ? 'admin-users-toggle-btn--active' : ''}`}
                  onClick={() => {
                    setPage(1);
                    setLocked('all');
                  }}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`admin-users-toggle-btn ${locked === 'true' ? 'admin-users-toggle-btn--active' : ''}`}
                  onClick={() => {
                    setPage(1);
                    setLocked('true');
                  }}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={`admin-users-toggle-btn ${locked === 'false' ? 'admin-users-toggle-btn--active' : ''}`}
                  onClick={() => {
                    setPage(1);
                    setLocked('false');
                  }}
                >
                  No
                </button>
              </div>
            </div>
            <button type="button" className="btn btn-secondary btn-xs" onClick={loadUsers} disabled={loading}>
              Refresh
            </button>
          </div>

          {loading && users.length === 0 ? (
            <div className="admin-users-empty">Loading users…</div>
          ) : error ? (
            <div className="admin-users-empty">
              <strong>Error:</strong> {error}
            </div>
          ) : users.length === 0 ? (
            <div className="admin-users-empty">No users match your filters.</div>
          ) : (
            <div className="admin-users-table-wrap">
              <table className="admin-users-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Level</th>
                    <th>Quizzes</th>
                    <th>Role</th>
                    <th>Premium</th>
                    <th>Status</th>
                    <th>Last login</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const status = u.is_banned
                      ? 'Banned'
                      : isLocked(u)
                        ? 'Locked'
                        : 'Active';
                    return (
                      <tr
                        key={u.id}
                        className="admin-users-row"
                      >
                        <td data-label="User">
                          <div className="admin-users-user">
                            <div className="admin-users-user__name">
                              {u.username}{u.is_bot ? ' (bot)' : ''}
                            </div>
                            <div className="admin-users-user__sub">{u.email}</div>
                          </div>
                        </td>
                        <td data-label="Level">{u.current_level ?? '—'}</td>
                        <td data-label="Quizzes">{u.competitive_quizzes_count ?? 0}</td>
                        <td data-label="Role">{u.is_admin ? 'Admin' : 'User'}</td>
                        <td data-label="Premium">{u.is_premium ? 'Yes' : 'No'}</td>
                        <td data-label="Status">{status}</td>
                        <td data-label="Last login">{formatDateTime(u.last_login_at)}</td>
                        <td data-label="Joined">{formatDateTime(u.created_at)}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-secondary btn-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              openUser(u);
                            }}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {pagination && (
            <div className="admin-users-pagination">
              <button
                type="button"
                className="btn btn-ghost btn-xs"
                disabled={loading || page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>
              <span className="admin-users-pagination__label">
                Page {pagination.page} of {pagination.totalPages || 1}
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-xs"
                disabled={loading || page >= (pagination.totalPages || 1)}
                onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages || p + 1))}
              >
                Next
              </button>
            </div>
          )}
        </section>
      </div>

      {/* Modal Overlay - Render in portal to ensure it's above navbar */}
      {activeUser && createPortal(
        <div className="admin-users-modal-overlay" onClick={() => setActiveUser(null)}>
          <aside 
            className="admin-users-drawer admin-users-drawer--open"
            onClick={(e) => e.stopPropagation()}
          >
          {!activeUser ? (
            <div className="admin-users-drawer__empty">
              <div className="admin-users-drawer__empty-title">Select a user</div>
              <div className="admin-users-drawer__empty-subtitle">
                Click a row to view details and run actions.
              </div>
            </div>
          ) : (
            <div className="admin-users-drawer__content">
              <div className="admin-users-drawer__header">
                <div>
                  <div className="admin-users-drawer__title">
                    {activeUser.username}{activeUser.is_bot ? ' (bot)' : ''}
                  </div>
                  <div className="admin-users-drawer__subtitle">{activeUser.email}</div>
                </div>
                <button type="button" className="btn btn-ghost btn-xs" onClick={() => setActiveUser(null)}>
                  Close
                </button>
              </div>

              <div className="admin-users-drawer__tabs">
                {(['profile', 'security', 'badges', 'danger'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`admin-users-drawer__tab${drawerTab === t ? ' admin-users-drawer__tab--active' : ''}`}
                    onClick={() => setDrawerTab(t)}
                  >
                    {t === 'profile' ? 'Profile' : t === 'security' ? 'Security' : t === 'badges' ? 'Badges' : 'Danger'}
                  </button>
                ))}
              </div>

              {drawerTab === 'profile' && (
                <div className="admin-users-panel">
                  <div className="admin-users-field-row">
                    <label>Username</label>
                    <input
                      value={activeUser.username}
                      onChange={(e) => setActiveUser({ ...activeUser, username: e.target.value })}
                    />
                  </div>
                  <div className="admin-users-field-row">
                    <label>Email</label>
                    <input value={activeUser.email} onChange={(e) => setActiveUser({ ...activeUser, email: e.target.value })} />
                  </div>
                  <div className="admin-users-field-row">
                    <label>Geo region</label>
                    <select
                      value={activeUser.geo_region || ''}
                      onChange={(e) => {
                        const newRegion = e.target.value || null;
                        setActiveUser({ 
                          ...activeUser, 
                          geo_region: newRegion,
                          country: null, // Reset country when region changes
                          country_code: null // Reset country code when region changes
                        });
                      }}
                    >
                      <option value="">Select region…</option>
                      {(Object.keys(REGION_LABELS) as GeoRegion[]).map((region) => (
                        <option key={region} value={region}>
                          {REGION_LABELS[region]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="admin-users-field-row">
                    <label>Country</label>
                    <select
                      value={activeUser.country || ''}
                      onChange={(e) => {
                        const selectedCountry = availableCountries.find(c => c.name === e.target.value);
                        setActiveUser({ 
                          ...activeUser, 
                          country: e.target.value || null,
                          country_code: selectedCountry?.code || null
                        });
                      }}
                      disabled={!activeUser.geo_region || availableCountries.length === 0}
                    >
                      <option value="">Select country…</option>
                      {availableCountries.map((country) => (
                        <option key={country.code} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="admin-users-actions-row">
                    <button
                      type="button"
                      className="btn btn-primary btn-compact"
                      disabled={!!actionBusy}
                      onClick={() =>
                        updateUser({
                          username: activeUser.username,
                          email: activeUser.email,
                          geo_region: activeUser.geo_region as any,
                          country: activeUser.country as any,
                          country_code: activeUser.country_code as any
                        } as any)
                      }
                    >
                      Save profile
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-compact"
                      disabled={!!actionBusy}
                      onClick={() => updateUser({ is_premium: !activeUser.is_premium } as any)}
                    >
                      {activeUser.is_premium ? 'Remove premium' : 'Grant premium'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-compact"
                      disabled={!!actionBusy}
                      onClick={() => updateUser({ is_admin: !activeUser.is_admin } as any)}
                    >
                      {activeUser.is_admin ? 'Demote from admin' : 'Promote to admin'}
                    </button>
                  </div>
                </div>
              )}

              {drawerTab === 'security' && (
                <div className="admin-users-panel">
                  <div className="admin-users-kv">
                    <div><strong>Banned:</strong> {activeUser.is_banned ? `Yes (${formatDateTime(activeUser.banned_at)})` : 'No'}</div>
                    <div><strong>Locked until:</strong> {formatDateTime(activeUser.locked_until)}</div>
                    <div><strong>Failed logins:</strong> {activeUser.failed_login_attempts ?? 0}</div>
                    <div><strong>Last login:</strong> {formatDateTime(activeUser.last_login_at)}</div>
                  </div>

                  <div className="admin-users-actions-row">
                    <button
                      type="button"
                      className="btn btn-secondary btn-compact"
                      disabled={!!actionBusy}
                      onClick={() =>
                        runAction('lock', async () => {
                          await axios.post(`/api/admin/users/${activeUser.id}/lock`, {});
                          showSuccess('User locked.');
                        })
                      }
                    >
                      Lock (30d)
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-compact"
                      disabled={!!actionBusy}
                      onClick={() =>
                        runAction('unlock', async () => {
                          await axios.post(`/api/admin/users/${activeUser.id}/unlock`);
                          showSuccess('User unlocked.');
                        })
                      }
                    >
                      Unlock
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-compact"
                      disabled={!!actionBusy}
                      onClick={() =>
                        runAction('ban', async () => {
                          await axios.post(`/api/admin/users/${activeUser.id}/ban`);
                          showSuccess('User banned.');
                        })
                      }
                    >
                      Ban
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-compact"
                      disabled={!!actionBusy}
                      onClick={() =>
                        runAction('unban', async () => {
                          await axios.post(`/api/admin/users/${activeUser.id}/unban`);
                          showSuccess('User unbanned.');
                        })
                      }
                    >
                      Unban
                    </button>
                  </div>

                  <div className="admin-users-divider" />

                  <div className="admin-users-actions-row">
                    <button
                      type="button"
                      className="btn btn-primary btn-compact"
                      disabled={!!actionBusy}
                      onClick={() =>
                        runAction('reset_email', async () => {
                          await axios.post(`/api/admin/users/${activeUser.id}/reset-password-email`);
                          showSuccess('Password reset email triggered (if eligible).');
                        })
                      }
                    >
                      Send reset password email
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-compact"
                      disabled={!!actionBusy}
                      onClick={() =>
                        runAction('temp_password', async () => {
                          const pwd = window.prompt(
                            'Enter a temporary password (min 8 chars, must include upper/lower/number/special).'
                          );
                          if (!pwd) return;
                          await axios.post(`/api/admin/users/${activeUser.id}/set-temp-password`, { password: pwd });
                          showSuccess('Temporary password set and sessions invalidated.');
                        })
                      }
                    >
                      Set temp password…
                    </button>
                  </div>
                </div>
              )}

              {drawerTab === 'badges' && (
                <div className="admin-users-panel">
                  <div className="admin-users-field-row">
                    <label>Grant badge</label>
                    <select value={badgeGrantId} onChange={(e) => setBadgeGrantId(e.target.value)}>
                      <option value="">Select badge…</option>
                      {badgesCatalog.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name} ({b.category})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="admin-users-actions-row">
                    <button
                      type="button"
                      className="btn btn-primary btn-compact"
                      disabled={!badgeGrantId || !!actionBusy}
                      onClick={() =>
                        runAction('badge_grant', async () => {
                          await axios.post(`/api/admin/users/${activeUser.id}/badges/grant`, { badgeId: badgeGrantId });
                          showSuccess('Badge granted (or already owned).');
                          await loadBadges();
                        })
                      }
                    >
                      Grant
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-compact"
                      disabled={!!actionBusy}
                      onClick={loadBadges}
                    >
                      Refresh badges
                    </button>
                  </div>

                  <div className="admin-users-divider" />

                  <div className="admin-users-badge-list">
                    <div className="admin-users-badge-list__title">User badges ({userBadges.length})</div>
                    {userBadges.length === 0 ? (
                      <div className="admin-users-empty">No badges.</div>
                    ) : (
                      userBadges.map((ub) => (
                        <div key={ub.id} className="admin-users-badge-row">
                          <div className="admin-users-badge-row__name">
                            {ub.badge?.name || ub.badgeId}
                            {ub.isFeatured ? ' ★' : ''}
                          </div>
                          <div className="admin-users-badge-row__meta">{formatDateTime(ub.earnedAt)}</div>
                          <div className="admin-users-badge-row__actions">
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs"
                              disabled={!!actionBusy}
                              onClick={() =>
                                runAction('badge_revoke', async () => {
                                  await axios.post(`/api/admin/users/${activeUser.id}/badges/revoke`, { badgeId: ub.badgeId });
                                  showSuccess('Badge revoked.');
                                  await loadBadges();
                                })
                              }
                            >
                              Revoke
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {drawerTab === 'danger' && (
                <div className="admin-users-panel">
                  <div className="admin-users-actions-row">
                    <button
                      type="button"
                      className="btn btn-secondary btn-compact"
                      disabled={!!actionBusy}
                      onClick={() =>
                        runAction('export', async () => {
                          await downloadExport();
                          showSuccess('Export downloaded.');
                        })
                      }
                    >
                      Export user data
                    </button>
                  </div>

                  <div className="admin-users-divider" />

                  <div className="admin-users-danger">
                    <div className="admin-users-danger__title">Delete</div>
                    <div className="admin-users-danger__subtitle">
                      Soft delete locks the account indefinitely. Hard delete removes the user and cascades deletes.
                    </div>
                    <div className="admin-users-actions-row">
                      <button
                        type="button"
                        className="btn btn-secondary btn-compact"
                        disabled={!!actionBusy}
                        onClick={() =>
                          runAction('soft_delete', async () => {
                            const ok = window.confirm(`Soft delete (lock) "${activeUser.username}"?`);
                            if (!ok) return;
                            const doDelete = async (retry = false) => {
                              const token = await ensureCsrfToken(retry);
                              const headers: Record<string, string> = { 'X-Confirm-Action': 'DELETE_USER' };
                              if (token) headers['X-CSRF-Token'] = token;
                              return axios.delete(`/api/admin/users/${activeUser.id}`, { headers });
                            };
                            try {
                              await doDelete();
                            } catch (e: any) {
                              if (e?.response?.status === 403 && e?.response?.data?.code?.includes?.('CSRF')) {
                                await doDelete(true);
                              } else throw e;
                            }
                            showSuccess('User soft-deleted (locked).');
                            setActiveUser(null);
                          })
                        }
                      >
                        Soft delete (lock)
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-compact"
                        disabled={!!actionBusy}
                        onClick={() =>
                          runAction('hard_delete', async () => {
                            const ok = window.confirm(`Hard delete "${activeUser.username}" permanently?`);
                            if (!ok) return;
                            const doDelete = async (retry = false) => {
                              const token = await ensureCsrfToken(retry);
                              const headers: Record<string, string> = { 'X-Confirm-Action': 'DELETE_USER' };
                              if (token) headers['X-CSRF-Token'] = token;
                              return axios.delete(`/api/admin/users/${activeUser.id}`, {
                                params: { hard: true },
                                headers
                              });
                            };
                            try {
                              await doDelete();
                            } catch (e: any) {
                              if (e?.response?.status === 403 && e?.response?.data?.code?.includes?.('CSRF')) {
                                await doDelete(true);
                              } else throw e;
                            }
                            showSuccess('User deleted permanently.');
                            setActiveUser(null);
                          })
                        }
                      >
                        Hard delete
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </aside>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AdminUsers;


